import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ApiMessageEngagement7d,
  ApiMessageHistoryItem,
  ApiMessageHistoryStatusFilter,
  ApiMessageTokenOption,
  downloadApiMessageHistoryCsv,
  fetchApiMessageHistory,
  parseApiTokenIdQuery,
  retryApiMessage,
  retryFailedApiMessages,
  skipReasonLabel,
} from "../../helpers/api_messages_helper";
import { apiErrorMessage } from "../../helpers/automations_helper";
import { getSendingSetup } from "../../helpers/sending_setup_helper";

type Props = {
  source: "api" | "welcome";
  titleKey: string;
  hintKey: string;
  /** Toast 1× por id quando status=failed (Tokens / API). */
  notifyFailed?: boolean;
  /** Deep-link / filtro inicial (ex.: ?api_messages=failed). */
  initialStatus?: ApiMessageHistoryStatusFilter;
  emptyCtaTo?: string;
  emptyCtaLabelKey?: string;
  emptyHintKey?: string;
  /** CTA sem navegar (ex.: abrir modal Criar token). */
  onEmptyCta?: () => void;
  /** Filtro por token — só source=api. */
  tokenOptions?: ApiMessageTokenOption[];
  /** Sincroniza filtro token com ?api_token_id= na URL. */
  syncTokenToUrl?: boolean;
  /** Callback quando o filtro de token muda (curl / pai). */
  onTokenFilterChange?: (tokenId: number | null) => void;
};

const FAILED_TOAST_STORAGE_KEY = "brenvio:api_messages_failed_toasted";
const POLL_MS = 30000;
/** Hub Tokens — histórico de POST /messages (não Conta). */
const HISTORY_HREF = "/settings/api-tokens?api_messages=failed";

function readToastedIds(): Set<number> {
  try {
    const raw = sessionStorage.getItem(FAILED_TOAST_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((n): n is number => typeof n === "number" && n > 0)
    );
  } catch {
    return new Set();
  }
}

function rememberToastedIds(ids: Set<number>): void {
  const list = Array.from(ids).slice(-200);
  try {
    sessionStorage.setItem(FAILED_TOAST_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

/**
 * Tabela de envios 1:1 — filtro, toast falha, poll, CSV, drawer, retry, datas.
 */
const ApiMessageHistoryCard = ({
  source,
  titleKey,
  hintKey,
  notifyFailed = false,
  initialStatus = "all",
  emptyCtaTo,
  emptyCtaLabelKey,
  emptyHintKey,
  onEmptyCta,
  tokenOptions,
  syncTokenToUrl = false,
  onTokenFilterChange,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [bulkRetrying, setBulkRetrying] = useState(false);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ApiMessageHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [failedToday, setFailedToday] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [oldestPendingAgeSeconds, setOldestPendingAgeSeconds] = useState<
    number | null
  >(null);
  const [replyToMissing, setReplyToMissing] = useState(false);
  const [engagement, setEngagement] = useState<ApiMessageEngagement7d | null>(
    null
  );
  const [statusFilter, setStatusFilter] =
    useState<ApiMessageHistoryStatusFilter>(initialStatus);
  const urlTokenId =
    syncTokenToUrl && source === "api"
      ? parseApiTokenIdQuery(searchParams.get("api_token_id"))
      : null;
  const [tokenFilter, setTokenFilter] = useState<number | "">(
    urlTokenId ?? ""
  );
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [detail, setDetail] = useState<ApiMessageHistoryItem | null>(null);
  const toastedRef = useRef<Set<number>>(readToastedIds());
  const statusRef = useRef(statusFilter);
  const pageRef = useRef(page);
  const fromRef = useRef(createdFrom);
  const toRef = useRef(createdTo);
  const tokenRef = useRef(tokenFilter);
  statusRef.current = statusFilter;
  pageRef.current = page;
  fromRef.current = createdFrom;
  toRef.current = createdTo;
  tokenRef.current = tokenFilter;

  const showTokenFilter =
    source === "api" && Array.isArray(tokenOptions) && tokenOptions.length > 0;

  useEffect(() => {
    setStatusFilter(initialStatus);
  }, [initialStatus]);

  // Banner: From SES sem Reply-To → respostas do cliente somem (API avulsa).
  useEffect(() => {
    if (source !== "api") {
      setReplyToMissing(false);
      return;
    }
    let cancelled = false;
    getSendingSetup()
      .then((res) => {
        if (cancelled || !res?.success || !res.data) return;
        const reply = (res.data.reply_to || "").trim();
        setReplyToMissing(reply === "");
      })
      .catch(() => {
        if (!cancelled) setReplyToMissing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  // Deep-link ?api_token_id= — alinha estado interno.
  useEffect(() => {
    if (!syncTokenToUrl || source !== "api") return;
    const fromUrl = parseApiTokenIdQuery(searchParams.get("api_token_id"));
    setTokenFilter(fromUrl ?? "");
  }, [syncTokenToUrl, source, searchParams]);

  useEffect(() => {
    onTokenFilterChange?.(
      typeof tokenFilter === "number" ? tokenFilter : null
    );
  }, [tokenFilter, onTokenFilterChange]);

  const setTokenFilterAndUrl = (next: number | "") => {
    setTokenFilter(next);
    if (!syncTokenToUrl || source !== "api") return;
    const n = new URLSearchParams(searchParams);
    if (typeof next === "number" && next > 0) {
      n.set("api_token_id", String(next));
    } else {
      n.delete("api_token_id");
    }
    setSearchParams(n, { replace: true });
  };

  const maybeToastFailures = useCallback(
    (rows: ApiMessageHistoryItem[]) => {
      if (!notifyFailed || source !== "api") return;
      let changed = false;
      for (const row of rows) {
        if (row.status !== "failed") continue;
        if (toastedRef.current.has(row.id)) continue;
        toastedRef.current.add(row.id);
        changed = true;
        toast.error(
          <div>
            <div>
              {t("apiMessages.history.failedToast", {
                email: row.to_email,
                subject: row.subject,
              })}
            </div>
            <Link
              to={HISTORY_HREF}
              className="text-white text-decoration-underline small"
              onClick={() => toast.dismiss(`api-msg-failed-${row.id}`)}
            >
              {t("apiMessages.history.failedToastCta")}
            </Link>
          </div>,
          { toastId: `api-msg-failed-${row.id}` }
        );
      }
      if (changed) rememberToastedIds(toastedRef.current);
    },
    [notifyFailed, source, t]
  );

  const load = useCallback(
    async (
      p: number,
      status: ApiMessageHistoryStatusFilter,
      from: string,
      to: string,
      tokenId: number | "",
      silent = false
    ) => {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await fetchApiMessageHistory({
          source,
          page: p,
          per_page: 10,
          status,
          created_from: from || undefined,
          created_to: to || undefined,
          api_token_id: typeof tokenId === "number" ? tokenId : null,
        });
        const rows = res.data.items || [];
        setItems(rows);
        setPage(res.data.meta.current_page);
        setLastPage(res.data.meta.last_page);
        setTotal(res.data.meta.total);
        setFailedToday(res.data.meta.failed_today ?? 0);
        setPendingCount(res.data.meta.pending_count ?? 0);
        setOldestPendingAgeSeconds(
          typeof res.data.meta.oldest_pending_age_seconds === "number"
            ? res.data.meta.oldest_pending_age_seconds
            : null
        );
        setEngagement(res.data.meta.engagement_7d ?? null);
        maybeToastFailures(rows);

        if (notifyFailed && source === "api" && status !== "failed") {
          try {
            const failedRes = await fetchApiMessageHistory({
              source: "api",
              page: 1,
              per_page: 10,
              status: "failed",
              api_token_id: typeof tokenId === "number" ? tokenId : null,
            });
            maybeToastFailures(failedRes.data.items || []);
            if (typeof failedRes.data.meta.failed_today === "number") {
              setFailedToday(failedRes.data.meta.failed_today);
            }
          } catch {
            // não bloqueia a lista principal
          }
        }
      } catch (e) {
        if (!silent) {
          setError(apiErrorMessage(e, t("apiMessages.history.loadError")));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [source, t, maybeToastFailures, notifyFailed]
  );

  useEffect(() => {
    void load(1, statusFilter, createdFrom, createdTo, tokenFilter);
  }, [load, statusFilter, createdFrom, createdTo, tokenFilter]);

  useEffect(() => {
    const shouldPoll = pendingCount > 0 || notifyFailed;
    if (!shouldPoll) return undefined;

    const id = window.setInterval(() => {
      void load(
        pageRef.current,
        statusRef.current,
        fromRef.current,
        toRef.current,
        tokenRef.current,
        true
      );
    }, POLL_MS);

    return () => window.clearInterval(id);
  }, [pendingCount, notifyFailed, load]);

  const fmt = (iso: string | null) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(i18n.language || "pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "sent":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "danger";
      case "skipped":
        return "secondary";
      default:
        return "light";
    }
  };

  const tokenLabel = (row: ApiMessageHistoryItem): string => {
    if (row.api_token?.name) {
      const prefix = row.api_token.token_prefix
        ? ` (${row.api_token.token_prefix}…)`
        : "";
      return `${row.api_token.name}${prefix}`;
    }
    return t("apiMessages.history.unknownToken");
  };

  const onStatusChange = (value: string) => {
    const next = (
      ["all", "pending", "sent", "failed", "skipped"].includes(value)
        ? value
        : "all"
    ) as ApiMessageHistoryStatusFilter;
    setStatusFilter(next);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadApiMessageHistoryCsv({
        source,
        status: statusFilter,
        created_from: createdFrom || undefined,
        created_to: createdTo || undefined,
        api_token_id: typeof tokenFilter === "number" ? tokenFilter : null,
      });
      toast.success(t("apiMessages.history.exportDone"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("apiMessages.history.exportError")));
    } finally {
      setExporting(false);
    }
  };

  const onBulkRetry = async () => {
    if (source !== "api") return;
    setBulkRetrying(true);
    try {
      const res = await retryFailedApiMessages({
        api_token_id: typeof tokenFilter === "number" ? tokenFilter : null,
        limit: 25,
      });
      const n = res.data?.retried_count ?? 0;
      toast.success(
        res.message || t("apiMessages.history.bulkRetryDone", { count: n })
      );
      if ((res.data?.error_count ?? 0) > 0) {
        toast.warning(
          t("apiMessages.history.bulkRetryPartial", {
            count: res.data.error_count,
          })
        );
      }
      void load(1, statusFilter, createdFrom, createdTo, tokenFilter);
    } catch (e) {
      toast.error(apiErrorMessage(e, t("apiMessages.history.bulkRetryError")));
    } finally {
      setBulkRetrying(false);
    }
  };

  const onRetry = async (row: ApiMessageHistoryItem) => {
    setRetryingId(row.id);
    try {
      const res = await retryApiMessage(row.id);
      toast.success(t("apiMessages.history.retryDone"));
      setDetail(res.data);
      void load(page, statusFilter, createdFrom, createdTo, tokenFilter, true);
    } catch (e) {
      toast.error(apiErrorMessage(e, t("apiMessages.history.retryError")));
    } finally {
      setRetryingId(null);
    }
  };

  const engagementLine =
    engagement && engagement.sent > 0
      ? t("apiMessages.history.engagement7d", {
          sent: engagement.sent,
          opened: engagement.opened,
          clicked: engagement.clicked,
        })
      : null;

  const showEmptyCta =
    statusFilter === "all" &&
    !createdFrom &&
    !createdTo &&
    tokenFilter === "" &&
    !!emptyCtaLabelKey &&
    (!!emptyCtaTo || !!onEmptyCta);

  return (
    <Card className="mt-3" id={`api-messages-history-${source}`}>
      <CardHeader>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div>
            <h5 className="card-title mb-1">
              {t(titleKey)}
              {source === "api" && failedToday > 0 ? (
                <Badge color="danger" className="ms-2 align-middle">
                  {t("apiMessages.history.failedToday", { count: failedToday })}
                </Badge>
              ) : null}
            </h5>
            <p className="text-muted small mb-0">{t(hintKey)}</p>
            {pendingCount > 0 ? (
              <p className="text-muted small mb-0 mt-1">
                {t("apiMessages.history.pendingHint", { count: pendingCount })}
              </p>
            ) : null}
            {engagementLine ? (
              <p className="text-muted small mb-0 mt-1">{engagementLine}</p>
            ) : null}
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            {showTokenFilter ? (
              <FormGroup className="mb-0" style={{ minWidth: 160 }}>
                <Label
                  for={`api-msg-token-${source}`}
                  className="visually-hidden"
                >
                  {t("apiMessages.history.filterToken")}
                </Label>
                <Input
                  id={`api-msg-token-${source}`}
                  type="select"
                  bsSize="sm"
                  value={tokenFilter === "" ? "" : String(tokenFilter)}
                  disabled={loading}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTokenFilterAndUrl(v === "" ? "" : Number(v));
                  }}
                  aria-label={t("apiMessages.history.filterToken")}
                >
                  <option value="">
                    {t("apiMessages.history.filterTokenAll")}
                  </option>
                  {tokenOptions!.map((tok) => (
                    <option key={tok.id} value={tok.id}>
                      {tok.name}
                      {tok.token_prefix ? ` (${tok.token_prefix}…)` : ""}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            ) : null}
            <FormGroup className="mb-0" style={{ minWidth: 140 }}>
              <Label
                for={`api-msg-status-${source}`}
                className="visually-hidden"
              >
                {t("apiMessages.history.filterStatus")}
              </Label>
              <Input
                id={`api-msg-status-${source}`}
                type="select"
                bsSize="sm"
                value={statusFilter}
                disabled={loading}
                onChange={(e) => onStatusChange(e.target.value)}
                aria-label={t("apiMessages.history.filterStatus")}
              >
                <option value="all">
                  {t("apiMessages.history.filterAll")}
                </option>
                <option value="sent">
                  {t("apiMessages.history.status.sent")}
                </option>
                <option value="failed">
                  {t("apiMessages.history.status.failed")}
                </option>
                <option value="pending">
                  {t("apiMessages.history.status.pending")}
                </option>
                <option value="skipped">
                  {t("apiMessages.history.status.skipped")}
                </option>
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label
                for={`api-msg-from-${source}`}
                className="visually-hidden"
              >
                {t("apiMessages.history.dateFrom")}
              </Label>
              <Input
                id={`api-msg-from-${source}`}
                type="date"
                bsSize="sm"
                value={createdFrom}
                disabled={loading}
                onChange={(e) => setCreatedFrom(e.target.value)}
                aria-label={t("apiMessages.history.dateFrom")}
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label for={`api-msg-to-${source}`} className="visually-hidden">
                {t("apiMessages.history.dateTo")}
              </Label>
              <Input
                id={`api-msg-to-${source}`}
                type="date"
                bsSize="sm"
                value={createdTo}
                disabled={loading}
                onChange={(e) => setCreatedTo(e.target.value)}
                aria-label={t("apiMessages.history.dateTo")}
              />
            </FormGroup>
            <Button
              color="light"
              size="sm"
              disabled={exporting || loading || total === 0}
              onClick={() => void onExport()}
            >
              {exporting ? (
                <Spinner size="sm" />
              ) : (
                t("apiMessages.history.exportCsv")
              )}
            </Button>
            {source === "api" && failedToday > 0 ? (
              <Button
                color="warning"
                size="sm"
                disabled={bulkRetrying || loading}
                onClick={() => void onBulkRetry()}
              >
                {bulkRetrying ? (
                  <Spinner size="sm" />
                ) : (
                  t("apiMessages.history.bulkRetry", {
                    count: Math.min(failedToday, 25),
                  })
                )}
              </Button>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        {source === "api" && replyToMissing ? (
          <Alert color="warning" className="mb-3">
            {t("apiMessages.history.replyToMissing")}{" "}
            <Link to="/settings/sending" className="fw-medium">
              {t("apiMessages.history.replyToMissingCta")}
            </Link>
          </Alert>
        ) : null}

        {/* Fila da plataforma atrasada (>5 min) — leigo, sem jargão Redis/worker. */}
        {pendingCount > 0 &&
        oldestPendingAgeSeconds != null &&
        oldestPendingAgeSeconds >= 300 ? (
          <Alert color="warning" className="mb-3">
            {t("apiMessages.history.pendingStuckHint")}
          </Alert>
        ) : null}

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-3">
            <p className="text-muted mb-2">
              {t(
                emptyHintKey &&
                  statusFilter === "all" &&
                  !createdFrom &&
                  !createdTo &&
                  tokenFilter === ""
                  ? emptyHintKey
                  : "apiMessages.history.empty"
              )}
            </p>
            {showEmptyCta ? (
              onEmptyCta ? (
                <Button
                  color="primary"
                  outline
                  size="sm"
                  type="button"
                  onClick={onEmptyCta}
                >
                  {t(emptyCtaLabelKey!)}
                </Button>
              ) : (
                <Button
                  color="primary"
                  outline
                  size="sm"
                  tag={Link}
                  to={emptyCtaTo!}
                >
                  {t(emptyCtaLabelKey!)}
                </Button>
              )
            ) : null}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="table-nowrap align-middle mb-0" size="sm">
                <thead>
                  <tr>
                    <th>{t("apiMessages.history.colTo")}</th>
                    <th>{t("apiMessages.history.colSubject")}</th>
                    {source === "api" ? (
                      <th>{t("apiMessages.history.colToken")}</th>
                    ) : null}
                    <th>{t("apiMessages.history.colStatus")}</th>
                    <th>{t("apiMessages.history.colReason")}</th>
                    <th>{t("apiMessages.history.colSent")}</th>
                    <th>{t("apiMessages.history.colOpened")}</th>
                    <th>{t("apiMessages.history.colClicked")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr
                      key={row.id}
                      role="button"
                      style={{ cursor: "pointer" }}
                      onClick={() => setDetail(row)}
                    >
                      <td>{row.to_email}</td>
                      <td className="text-truncate" style={{ maxWidth: 180 }}>
                        {row.subject}
                      </td>
                      {source === "api" ? (
                        <td
                          className="text-muted small text-truncate"
                          style={{ maxWidth: 140 }}
                          title={tokenLabel(row)}
                        >
                          {tokenLabel(row)}
                        </td>
                      ) : null}
                      <td>
                        <Badge
                          color={statusColor(row.status)}
                          className="text-uppercase"
                        >
                          {t(`apiMessages.history.status.${row.status}`, {
                            defaultValue: row.status,
                          })}
                        </Badge>
                      </td>
                      <td
                        className="text-muted small text-truncate"
                        style={{ maxWidth: 140 }}
                        title={row.skip_reason || undefined}
                      >
                        {row.status === "failed" || row.status === "skipped"
                          ? skipReasonLabel(row.skip_reason, t)
                          : "—"}
                      </td>
                      <td>{fmt(row.sent_at)}</td>
                      <td>{fmt(row.opened_at)}</td>
                      <td>{fmt(row.clicked_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="text-muted small">
                {t("apiMessages.history.total", { count: total })}
              </span>
              <div className="d-flex gap-2">
                <Button
                  color="light"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() =>
                    void load(
                      page - 1,
                      statusFilter,
                      createdFrom,
                      createdTo,
                      tokenFilter
                    )
                  }
                >
                  {t("apiMessages.history.prev")}
                </Button>
                <Button
                  color="light"
                  size="sm"
                  disabled={page >= lastPage || loading}
                  onClick={() =>
                    void load(
                      page + 1,
                      statusFilter,
                      createdFrom,
                      createdTo,
                      tokenFilter
                    )
                  }
                >
                  {t("apiMessages.history.next")}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardBody>

      <Offcanvas
        direction="end"
        isOpen={!!detail}
        toggle={() => setDetail(null)}
      >
        <OffcanvasHeader toggle={() => setDetail(null)}>
          {t("apiMessages.history.detailTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {detail ? (
            <>
              <dl className="row mb-0 small">
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colTo")}
                </dt>
                <dd className="col-8">{detail.to_email}</dd>
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colSubject")}
                </dt>
                <dd className="col-8">{detail.subject}</dd>
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colStatus")}
                </dt>
                <dd className="col-8">
                  <Badge color={statusColor(detail.status)}>
                    {t(`apiMessages.history.status.${detail.status}`, {
                      defaultValue: detail.status,
                    })}
                  </Badge>
                </dd>
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colReason")}
                </dt>
                <dd className="col-8 text-break">
                  {detail.skip_reason
                    ? skipReasonLabel(detail.skip_reason, t)
                    : "—"}
                  {detail.skip_reason ? (
                    <div className="text-muted mt-1 font-monospace">
                      {detail.skip_reason}
                    </div>
                  ) : null}
                </dd>
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colType")}
                </dt>
                <dd className="col-8">{detail.message_type || "—"}</dd>
                {detail.source === "api" ? (
                  <>
                    <dt className="col-4 text-muted">
                      {t("apiMessages.history.colToken")}
                    </dt>
                    <dd className="col-8">{tokenLabel(detail)}</dd>
                  </>
                ) : null}
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colCreated")}
                </dt>
                <dd className="col-8">{fmt(detail.created_at)}</dd>
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colSent")}
                </dt>
                <dd className="col-8">{fmt(detail.sent_at)}</dd>
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colOpened")}
                </dt>
                <dd className="col-8">{fmt(detail.opened_at)}</dd>
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colClicked")}
                </dt>
                <dd className="col-8">{fmt(detail.clicked_at)}</dd>
                <dt className="col-4 text-muted">
                  {t("apiMessages.history.colProviderId")}
                </dt>
                <dd className="col-8 text-break font-monospace small">
                  {detail.provider_message_id || "—"}
                </dd>
              </dl>
              {detail.status === "failed" ? (
                <Button
                  color="primary"
                  className="mt-3"
                  disabled={retryingId === detail.id}
                  onClick={() => void onRetry(detail)}
                >
                  {retryingId === detail.id ? (
                    <Spinner size="sm" className="me-1" />
                  ) : null}
                  {t("apiMessages.history.retry")}
                </Button>
              ) : null}
            </>
          ) : null}
        </OffcanvasBody>
      </Offcanvas>
    </Card>
  );
};

export default ApiMessageHistoryCard;
